import React, { useState } from 'react';
import { User, Lock, Bell, Building2, Users, CreditCard, Code, ChevronRight, Mail, Phone, Shield, Key, Globe, Check, Plus, X, Trash2, Landmark, ChevronDown, ChevronUp, ArrowRightLeft, CheckCircle2, AlertCircle, Eye, EyeOff, Zap, BarChart3, Info, Palette, Upload, Image as ImageIcon, FileText, Triangle } from 'lucide-react';
import type { BrandingConfig } from '../App';
interface AchDetails {
  tenantId: number;
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  accountType: 'checking' | 'savings';
  accountHolder: string;
  autoBill: boolean;
  lastDebit?: string;
  lastDebitAmount?: number;
}
interface SettingsPageProps {
  branding?: BrandingConfig;
  onBrandingChange?: (branding: BrandingConfig) => void;
}
export function SettingsPage({
  branding,
  onBrandingChange
}: SettingsPageProps) {
  const [activeTab, setActiveTab] = useState('profile');
  // Local branding state that syncs with parent
  const brandName = branding?.brandName || 'Astrolayb';
  const primaryColor = branding?.primaryColor || '#22c55e';
  const logoUrl = branding?.logoUrl || '';
  const reportFooter = branding?.reportFooter || 'Confidential — Generated by Astrolayb Security Intelligence Platform';
  const removePoweredBy = branding?.removePoweredBy ?? false;
  const includeCoverPage = branding?.includeCoverPage ?? true;
  const customDomain = branding?.customDomain || '';
  const emailSenderName = branding?.emailSenderName || '';
  const updateBranding = (partial: Partial<BrandingConfig>) => {
    if (branding && onBrandingChange) {
      onBrandingChange({
        ...branding,
        ...partial
      });
    }
  };
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return; // 2MB limit
    const reader = new FileReader();
    reader.onload = event => {
      const dataUrl = event.target?.result as string;
      updateBranding({
        logoUrl: dataUrl
      });
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-uploaded
    e.target.value = '';
  };
  const handleRemoveLogo = () => {
    updateBranding({
      logoUrl: ''
    });
  };
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantPlan, setNewTenantPlan] = useState<'basic' | 'premium'>('premium');
  const [tenants, setTenants] = useState([{
    id: 1,
    name: 'Acme Corp',
    plan: 'premium' as const,
    status: 'active' as const
  }, {
    id: 2,
    name: 'TechStart Inc',
    plan: 'premium' as const,
    status: 'active' as const
  }, {
    id: 3,
    name: 'GlobalSecure LLC',
    plan: 'premium' as const,
    status: 'active' as const
  }, {
    id: 4,
    name: 'DataFlow Systems',
    plan: 'basic' as const,
    status: 'active' as const
  }, {
    id: 5,
    name: 'CloudNine Solutions',
    plan: 'premium' as const,
    status: 'active' as const
  }, {
    id: 6,
    name: 'NetGuard Pro',
    plan: 'premium' as const,
    status: 'active' as const
  }, {
    id: 7,
    name: 'SecureStack',
    plan: 'basic' as const,
    status: 'active' as const
  }, {
    id: 8,
    name: 'Vertex Digital',
    plan: 'premium' as const,
    status: 'active' as const
  }]);
  // ACH / Client Billing State
  const [achDetails, setAchDetails] = useState<AchDetails[]>([{
    tenantId: 1,
    bankName: 'JPMorgan Chase',
    routingNumber: '021000021',
    accountNumber: '****4829',
    accountType: 'checking',
    accountHolder: 'Acme Corp LLC',
    autoBill: true,
    lastDebit: 'Feb 1, 2026',
    lastDebitAmount: 499
  }, {
    tenantId: 2,
    bankName: 'Silicon Valley Bank',
    routingNumber: '121140399',
    accountNumber: '****6104',
    accountType: 'checking',
    accountHolder: 'TechStart Inc',
    autoBill: true,
    lastDebit: 'Feb 1, 2026',
    lastDebitAmount: 499
  }, {
    tenantId: 3,
    bankName: 'Bank of America',
    routingNumber: '026009593',
    accountNumber: '****7712',
    accountType: 'checking',
    accountHolder: 'GlobalSecure LLC',
    autoBill: true,
    lastDebit: 'Feb 1, 2026',
    lastDebitAmount: 499
  }, {
    tenantId: 5,
    bankName: 'Wells Fargo',
    routingNumber: '121000248',
    accountNumber: '****3301',
    accountType: 'checking',
    accountHolder: 'CloudNine Solutions Inc',
    autoBill: true,
    lastDebit: 'Feb 1, 2026',
    lastDebitAmount: 499
  }, {
    tenantId: 6,
    bankName: 'Citibank',
    routingNumber: '021000089',
    accountNumber: '****8855',
    accountType: 'checking',
    accountHolder: 'NetGuard Pro LLC',
    autoBill: true,
    lastDebit: 'Feb 1, 2026',
    lastDebitAmount: 499
  }, {
    tenantId: 8,
    bankName: 'US Bank',
    routingNumber: '091000019',
    accountNumber: '****2290',
    accountType: 'checking',
    accountHolder: 'Vertex Digital Corp',
    autoBill: true,
    lastDebit: 'Feb 1, 2026',
    lastDebitAmount: 499
  }]);
  const [expandedAchTenant, setExpandedAchTenant] = useState<number | null>(1);
  const [editingAch, setEditingAch] = useState<number | null>(null);
  const [achForm, setAchForm] = useState({
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking' as 'checking' | 'savings',
    accountHolder: '',
    autoBill: true
  });
  const [showAccountNumbers, setShowAccountNumbers] = useState<Record<number, boolean>>({});
  const startAchEdit = (tenantId: number) => {
    const existing = achDetails.find(a => a.tenantId === tenantId);
    if (existing) {
      setAchForm({
        bankName: existing.bankName,
        routingNumber: existing.routingNumber,
        accountNumber: '',
        accountType: existing.accountType,
        accountHolder: existing.accountHolder,
        autoBill: existing.autoBill
      });
    } else {
      const tenant = tenants.find(t => t.id === tenantId);
      setAchForm({
        bankName: '',
        routingNumber: '',
        accountNumber: '',
        accountType: 'checking',
        accountHolder: tenant?.name || '',
        autoBill: true
      });
    }
    setEditingAch(tenantId);
    setExpandedAchTenant(tenantId);
  };
  const saveAchDetails = (tenantId: number) => {
    if (!achForm.bankName.trim() || !achForm.routingNumber.trim() || !achForm.accountNumber.trim()) return;
    const masked = '****' + achForm.accountNumber.slice(-4);
    setAchDetails(prev => {
      const existing = prev.findIndex(a => a.tenantId === tenantId);
      const entry: AchDetails = {
        tenantId,
        bankName: achForm.bankName,
        routingNumber: achForm.routingNumber,
        accountNumber: masked,
        accountType: achForm.accountType,
        accountHolder: achForm.accountHolder,
        autoBill: achForm.autoBill
      };
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = entry;
        return next;
      }
      return [...prev, entry];
    });
    setEditingAch(null);
  };
  const removeAchDetails = (tenantId: number) => {
    setAchDetails(prev => prev.filter(a => a.tenantId !== tenantId));
    setEditingAch(null);
    setExpandedAchTenant(null);
  };
  const toggleAutoBill = (tenantId: number) => {
    setAchDetails(prev => prev.map(a => a.tenantId === tenantId ? {
      ...a,
      autoBill: !a.autoBill
    } : a));
  };
  const handleAddTenant = () => {
    if (!newTenantName.trim()) return;
    setTenants(prev => [...prev, {
      id: Date.now(),
      name: newTenantName.trim(),
      plan: newTenantPlan,
      status: 'active' as const
    }]);
    setNewTenantName('');
    setNewTenantPlan('premium');
    setShowAddTenant(false);
  };
  const handleRemoveTenant = (id: number) => {
    setTenants(prev => prev.filter(t => t.id !== id));
  };
  const monthlyTotal = tenants.reduce((sum, t) => sum + (t.plan === 'premium' ? 499 : 99), 0);
  const premiumCount = tenants.filter(t => t.plan === 'premium').length;
  const basicCount = tenants.filter(t => t.plan === 'basic').length;
  const tabs = [{
    id: 'profile',
    label: 'My Profile',
    subtitle: 'Personal information',
    icon: <User size={18} data-id="element-1763" />
  }, {
    id: 'security',
    label: 'Security',
    subtitle: 'Password & MFA',
    icon: <Lock size={18} data-id="element-1764" />
  }, {
    id: 'notifications',
    label: 'Notifications',
    subtitle: 'Email & alerts',
    icon: <Bell size={18} data-id="element-1765" />
  }, {
    id: 'organization',
    label: 'Organization',
    subtitle: 'Company settings',
    icon: <Building2 size={18} data-id="element-1766" />
  }, {
    id: 'branding',
    label: 'White Label',
    subtitle: 'Custom branding',
    icon: <Palette size={18} data-id="element-1767" />
  }, {
    id: 'team',
    label: 'Team Members',
    subtitle: 'Users & roles',
    icon: <Users size={18} data-id="element-1768" />
  }, {
    id: 'billing',
    label: 'Billing',
    subtitle: 'Plans & invoices',
    icon: <CreditCard size={18} data-id="element-1769" />
  }, {
    id: 'api',
    label: 'API & Integrations',
    subtitle: 'Keys & webhooks',
    icon: <Code size={18} data-id="element-1770" />
  }];
  return <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-1771">
      {/* Page Header */}
      <div className="mb-8" data-id="element-1772">
        <h1 className="text-3xl font-bold text-white mb-2" data-id="element-1773">Settings</h1>
        <p className="text-astro-text-secondary text-lg" data-id="element-1774">
          Manage your account and organization preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8" data-id="element-1775">
        {/* Left Sidebar */}
        <div className="w-full lg:w-64 shrink-0 space-y-1" data-id="element-1776">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-all group ${activeTab === tab.id ? 'bg-astro-green-dim border border-astro-green/20 text-white' : 'text-astro-text-secondary hover:text-white hover:bg-white/[0.04] border border-transparent'}`} data-id="element-1777">
              <div className="flex items-center gap-3" data-id="element-1778">
                <div className={`${activeTab === tab.id ? 'text-astro-green' : 'text-astro-text-secondary group-hover:text-white'}`} data-id="element-1779">
                  {tab.icon}
                </div>
                <div data-id="element-1780">
                  <div className="text-sm font-medium" data-id="element-1781">{tab.label}</div>
                  <div className="text-xs opacity-70" data-id="element-1782">{tab.subtitle}</div>
                </div>
              </div>
              {activeTab === tab.id && <ChevronRight size={16} className="text-astro-green" data-id="element-1783" />}
            </button>)}
        </div>

        {/* Right Content */}
        <div className="flex-1 space-y-6" data-id="element-1784">
          {activeTab === 'profile' && <>
              <div className="flex items-center justify-between mb-2" data-id="element-1785">
                <div data-id="element-1786">
                  <h2 className="text-xl font-bold text-white" data-id="element-1787">My Profile</h2>
                  <p className="text-sm text-astro-text-secondary" data-id="element-1788">
                    Manage your personal information
                  </p>
                </div>
                <button className="px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-1789">
                  Edit Profile
                </button>
              </div>

              {/* Profile Card */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6 flex items-center gap-6" data-id="element-1790">
                <div className="w-24 h-24 rounded-full bg-astro-green flex items-center justify-center text-black" data-id="element-1791">
                  <User size={40} data-id="element-1792" />
                </div>
                <div data-id="element-1793">
                  <h3 className="text-2xl font-bold text-white" data-id="element-1794">Sarah Chen</h3>
                  <p className="text-astro-text-secondary font-medium mb-1" data-id="element-1795">
                    Administrator
                  </p>
                  <p className="text-xs text-astro-text-secondary opacity-70" data-id="element-1796">
                    Member since January 2024
                  </p>
                </div>
              </div>

              {/* Personal Info Form */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1797">
                <h3 className="text-lg font-bold text-white mb-6" data-id="element-1798">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1799">
                  <div data-id="element-1800">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1801">
                      First Name
                    </label>
                    <input type="text" defaultValue="Sarah" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1802" />
                  </div>
                  <div data-id="element-1803">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1804">
                      Last Name
                    </label>
                    <input type="text" defaultValue="Chen" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1805" />
                  </div>
                  <div data-id="element-1806">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1807">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-1808">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-1809" />

                      <input type="email" defaultValue="sarah.chen@astrolayb.com" className="w-full bg-[#0a0e14] border border-astro-border rounded pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1810" />
                    </div>
                  </div>
                  <div data-id="element-1811">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1812">
                      Phone Number
                    </label>
                    <div className="relative" data-id="element-1813">
                      <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-1814" />

                      <input type="tel" defaultValue="+1 (555) 123-4567" className="w-full bg-[#0a0e14] border border-astro-border rounded pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1815" />
                    </div>
                  </div>
                  <div className="md:col-span-2" data-id="element-1816">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1817">
                      Job Title
                    </label>
                    <input type="text" defaultValue="Security Operations Manager" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1818" />
                  </div>
                  <div className="md:col-span-2" data-id="element-1819">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1820">
                      Timezone
                    </label>
                    <select className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1821">
                      <option data-id="element-1822">America/Los_Angeles (PST)</option>
                      <option data-id="element-1823">America/New_York (EST)</option>
                      <option data-id="element-1824">Europe/London (GMT)</option>
                      <option data-id="element-1825">Asia/Tokyo (JST)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border border-astro-danger/30 bg-astro-danger/5 rounded-lg p-6" data-id="element-1826">
                <h3 className="text-lg font-bold text-astro-danger mb-2" data-id="element-1827">
                  Danger Zone
                </h3>
                <p className="text-sm text-astro-text-secondary mb-4" data-id="element-1828">
                  Once you delete your account, there is no going back. Please
                  be certain.
                </p>
                <button className="px-4 py-2 text-astro-danger border border-astro-danger/30 rounded text-sm font-medium hover:bg-astro-danger/10 transition-colors" data-id="element-1829">
                  Delete Account
                </button>
              </div>
            </>}

          {activeTab === 'security' && <>
              <div className="mb-2" data-id="element-1830">
                <h2 className="text-xl font-bold text-white" data-id="element-1831">Security</h2>
                <p className="text-sm text-astro-text-secondary" data-id="element-1832">
                  Manage your password and multi-factor authentication
                </p>
              </div>

              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1833">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-1834">
                  <Lock size={20} className="text-astro-green" data-id="element-1835" /> Password
                </h3>
                <div className="space-y-4 max-w-md" data-id="element-1836">
                  <div data-id="element-1837">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1838">
                      Current Password
                    </label>
                    <input type="password" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1839" />
                  </div>
                  <div data-id="element-1840">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1841">
                      New Password
                    </label>
                    <input type="password" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1842" />
                  </div>
                  <div data-id="element-1843">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1844">
                      Confirm New Password
                    </label>
                    <input type="password" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1845" />
                  </div>
                  <button className="px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-1846">
                    Update Password
                  </button>
                </div>
              </div>

              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1847">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-1848">
                  <Shield size={20} className="text-astro-green" data-id="element-1849" /> Multi-Factor
                  Authentication
                </h3>
                <p className="text-sm text-astro-text-secondary mb-6" data-id="element-1850">
                  Add an extra layer of security to your account by enabling
                  MFA.
                </p>
                <div className="flex items-center justify-between p-4 bg-[#0a0e14] border border-astro-border rounded-lg" data-id="element-1851">
                  <div className="flex items-center gap-3" data-id="element-1852">
                    <div className="w-10 h-10 rounded-full bg-astro-green/10 flex items-center justify-center text-astro-green" data-id="element-1853">
                      <Phone size={20} data-id="element-1854" />
                    </div>
                    <div data-id="element-1855">
                      <div className="text-sm font-bold text-white" data-id="element-1856">
                        Authenticator App
                      </div>
                      <div className="text-xs text-astro-text-secondary" data-id="element-1857">
                        Use Google Authenticator or similar
                      </div>
                    </div>
                  </div>
                  <button className="px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors" data-id="element-1858">
                    Enable
                  </button>
                </div>
              </div>
            </>}

          {activeTab === 'notifications' && <>
              <div className="mb-2" data-id="element-1859">
                <h2 className="text-xl font-bold text-white" data-id="element-1860">Notifications</h2>
                <p className="text-sm text-astro-text-secondary" data-id="element-1861">
                  Manage how you receive alerts and updates
                </p>
              </div>

              <div className="bg-astro-card border border-astro-border rounded-lg p-6 space-y-6" data-id="element-1862">
                {['Critical security alerts', 'New vulnerability findings', 'Weekly security digest', 'Scan completion reports', 'New device sign-ins'].map((item, i) => <div key={i} className="flex items-center justify-between py-2" data-id="element-1863">
                    <span className="text-sm font-medium text-white" data-id="element-1864">
                      {item}
                    </span>
                    <div className="relative inline-block w-10 h-6 align-middle select-none transition duration-200 ease-in" data-id="element-1865">
                      <input type="checkbox" defaultChecked={i < 3} className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer translate-x-0.5 translate-y-0.5 checked:translate-x-5 checked:bg-astro-green transition-transform duration-200" data-id="element-1866" />

                      <label className="toggle-label block overflow-hidden h-6 rounded-full bg-[#0a0e14] border border-astro-border cursor-pointer" data-id="element-1867"></label>
                    </div>
                  </div>)}
              </div>
            </>}

          {activeTab === 'organization' && <>
              <div className="mb-2" data-id="element-1868">
                <h2 className="text-xl font-bold text-white" data-id="element-1869">Organization</h2>
                <p className="text-sm text-astro-text-secondary" data-id="element-1870">
                  Manage your company details
                </p>
              </div>

              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1871">
                <div className="grid grid-cols-1 gap-6 max-w-md" data-id="element-1872">
                  <div data-id="element-1873">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1874">
                      Company Name
                    </label>
                    <div className="relative" data-id="element-1875">
                      <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-1876" />

                      <input type="text" defaultValue="Astrolayb Inc." className="w-full bg-[#0a0e14] border border-astro-border rounded pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1877" />
                    </div>
                  </div>
                  <div data-id="element-1878">
                    <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1879">
                      Primary Domain
                    </label>
                    <div className="relative" data-id="element-1880">
                      <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-1881" />

                      <input type="text" defaultValue="astrolayb.com" className="w-full bg-[#0a0e14] border border-astro-border rounded pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1882" />
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)] w-fit" data-id="element-1883">
                    Save Changes
                  </button>
                </div>
              </div>
            </>}

          {activeTab === 'branding' && <>
              <div className="mb-2 flex items-center justify-between" data-id="element-1884">
                <div data-id="element-1885">
                  <h2 className="text-xl font-bold text-white" data-id="element-1886">
                    White Label Branding
                  </h2>
                  <p className="text-sm text-astro-text-secondary" data-id="element-1887">
                    Customize the platform and reports with your own brand
                    identity
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-1888">
                  {brandName !== 'Astrolayb' && <button onClick={() => updateBranding({
                brandName: 'Astrolayb',
                primaryColor: '#22c55e',
                reportFooter: 'Confidential — Generated by Astrolayb Security Intelligence Platform',
                removePoweredBy: false,
                includeCoverPage: true,
                customDomain: '',
                emailSenderName: '',
                logoUrl: ''
              })} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-1889">
                      Reset to Default
                    </button>}
                  <div className="px-3 py-1.5 bg-astro-green/10 border border-astro-green/20 rounded text-[11px] font-bold text-astro-green uppercase tracking-wider" data-id="element-1890">
                    Live — changes apply instantly
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-id="element-1891">
                <div className="xl:col-span-2 space-y-6" data-id="element-1892">
                  {/* Visual Identity */}
                  <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1893">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-1894">
                      <Palette size={20} className="text-astro-green" data-id="element-1895" /> Visual
                      Identity
                    </h3>

                    <div className="space-y-6" data-id="element-1896">
                      <div data-id="element-1897">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-3" data-id="element-1898">
                          Company Logo
                        </label>
                        <div className="flex items-start gap-6" data-id="element-1899">
                          {logoUrl ? <div className="w-32 h-32 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center justify-center p-3 relative group" data-id="element-1900">
                              <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-id="element-1901" />

                              <button onClick={handleRemoveLogo} className="absolute -top-2 -right-2 w-6 h-6 bg-astro-danger rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" data-id="element-1902">
                                <X size={12} data-id="element-1903" />
                              </button>
                            </div> : <label className="w-32 h-32 bg-[#0a0e14] border-2 border-dashed border-astro-border rounded-lg flex flex-col items-center justify-center text-astro-text-secondary hover:border-astro-green/50 hover:text-white transition-colors cursor-pointer group" data-id="element-1904">
                              <Upload size={24} className="mb-2 group-hover:-translate-y-1 transition-transform" data-id="element-1905" />

                              <span className="text-xs font-medium" data-id="element-1906">
                                Upload Logo
                              </span>
                              <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" data-id="element-1907" />
                            </label>}
                          <div className="flex-1" data-id="element-1908">
                            <p className="text-xs text-astro-text-secondary mb-4 leading-relaxed" data-id="element-1909">
                              Upload a high-resolution logo to be used on the
                              dashboard header, login screen, and generated
                              reports.
                              <br data-id="element-1910" />
                              <br data-id="element-1911" />
                              Recommended size: 400x100px (PNG or SVG with
                              transparent background). Max file size: 2MB.
                            </p>
                            {logoUrl ? <div className="flex items-center gap-3" data-id="element-1912">
                                <div className="w-8 h-8 rounded bg-[#0a0e14] border border-astro-border flex items-center justify-center overflow-hidden" data-id="element-1913">
                                  <img src={logoUrl} alt="" className="w-6 h-6 object-contain" data-id="element-1914" />
                                </div>
                                <span className="text-sm text-white font-medium" data-id="element-1915">
                                  Custom logo uploaded
                                </span>
                                <button onClick={handleRemoveLogo} className="text-xs text-astro-danger hover:text-red-400 transition-colors ml-auto" data-id="element-1916">
                                  Remove
                                </button>
                              </div> : <div className="flex items-center gap-3" data-id="element-1917">
                                <div className="w-8 h-8 rounded bg-[#0a0e14] border border-astro-border flex items-center justify-center" data-id="element-1918">
                                  <ImageIcon size={14} className="text-astro-text-secondary" data-id="element-1919" />
                                </div>
                                <span className="text-sm text-astro-text-secondary font-medium" data-id="element-1920">
                                  No logo uploaded
                                </span>
                                <label className="text-xs text-astro-green hover:text-astro-green-dark transition-colors ml-auto cursor-pointer font-medium" data-id="element-1921">
                                  Browse files
                                  <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={handleLogoUpload} className="hidden" data-id="element-1922" />
                                </label>
                              </div>}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-astro-border" data-id="element-1923">
                        <div data-id="element-1924">
                          <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1925">
                            Display Name
                          </label>
                          <input type="text" value={brandName} onChange={e => updateBranding({
                        brandName: e.target.value
                      })} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1926" />

                          <p className="text-[10px] text-astro-text-secondary mt-1.5" data-id="element-1927">
                            Replaces "Astrolayb" in the navbar and report
                            titles.
                          </p>
                        </div>
                        <div data-id="element-1928">
                          <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1929">
                            Primary Brand Color
                          </label>
                          <div className="flex items-center gap-3" data-id="element-1930">
                            <div className="relative w-10 h-10 rounded border border-astro-border overflow-hidden shrink-0" data-id="element-1931">
                              <input type="color" value={primaryColor} onChange={e => updateBranding({
                            primaryColor: e.target.value
                          })} className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer" data-id="element-1932" />
                            </div>
                            <input type="text" value={primaryColor} onChange={e => updateBranding({
                          primaryColor: e.target.value
                        })} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors font-mono uppercase" data-id="element-1933" />
                          </div>
                          <p className="text-[10px] text-astro-text-secondary mt-1.5" data-id="element-1934">
                            Used for the navbar logo, active states, and report
                            accents.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Domain & Email */}
                  <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1935">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-1936">
                      <Globe size={20} className="text-astro-green" data-id="element-1937" /> Domain &
                      Communications
                    </h3>

                    <div className="space-y-6" data-id="element-1938">
                      <div data-id="element-1939">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1940">
                          Custom Domain
                        </label>
                        <div className="flex items-center gap-3" data-id="element-1941">
                          <input type="text" value={customDomain} onChange={e => updateBranding({
                        customDomain: e.target.value
                      })} placeholder="security.yourcompany.com" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1942" />

                          <button className="px-3 py-2 bg-[#0a0e14] border border-astro-border text-white text-sm font-medium rounded hover:border-white/30 transition-colors shrink-0" data-id="element-1943">
                            Verify DNS
                          </button>
                        </div>
                        {customDomain && <div className="flex items-center gap-2 mt-2" data-id="element-1944">
                            <CheckCircle2 size={12} className="text-astro-green" data-id="element-1945" />

                            <span className="text-[10px] text-astro-green font-medium" data-id="element-1946">
                              Domain verified and SSL active
                            </span>
                          </div>}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-astro-border" data-id="element-1947">
                        <div data-id="element-1948">
                          <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1949">
                            Email Sender Name
                          </label>
                          <input type="text" value={emailSenderName} onChange={e => updateBranding({
                        emailSenderName: e.target.value
                      })} placeholder={`${brandName} Security Alerts`} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1950" />
                        </div>
                        <div data-id="element-1951">
                          <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1952">
                            Reply-To Address
                          </label>
                          <input type="email" placeholder="support@yourcompany.com" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white placeholder:text-astro-text-secondary/50 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1953" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reports */}
                  <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-1954">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-1955">
                      <FileText size={20} className="text-astro-green" data-id="element-1956" /> Report
                      Settings
                    </h3>

                    <div className="space-y-6" data-id="element-1957">
                      <div data-id="element-1958">
                        <label className="block text-sm font-medium text-astro-text-secondary mb-1.5" data-id="element-1959">
                          Custom Footer Text
                        </label>
                        <input type="text" value={reportFooter} onChange={e => updateBranding({
                      reportFooter: e.target.value
                    })} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1960" />
                      </div>

                      <div className="flex items-center justify-between py-2" data-id="element-1961">
                        <div data-id="element-1962">
                          <span className="text-sm font-medium text-white block" data-id="element-1963">
                            Include Cover Page
                          </span>
                          <span className="text-xs text-astro-text-secondary" data-id="element-1964">
                            Add a branded cover page to all PDF exports
                          </span>
                        </div>
                        <button onClick={() => updateBranding({
                      includeCoverPage: !includeCoverPage
                    })} className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${includeCoverPage ? 'bg-astro-green' : 'bg-[#0a0e14] border border-astro-border'}`} data-id="element-1965">
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeCoverPage ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-1966" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between py-2" data-id="element-1967">
                        <div data-id="element-1968">
                          <span className="text-sm font-medium text-white block" data-id="element-1969">
                            Remove "Powered by Astrolayb"
                          </span>
                          <span className="text-xs text-astro-text-secondary" data-id="element-1970">
                            Hide platform attribution from reports
                          </span>
                        </div>
                        <button onClick={() => updateBranding({
                      removePoweredBy: !removePoweredBy
                    })} className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${removePoweredBy ? 'bg-astro-green' : 'bg-[#0a0e14] border border-astro-border'}`} data-id="element-1971">
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${removePoweredBy ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-1972" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Preview Sidebar */}
                <div className="xl:col-span-1" data-id="element-1973">
                  <div className="sticky top-20" data-id="element-1974">
                    <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider" data-id="element-1975">
                      Live Preview
                    </h3>

                    {/* Mock Report Header */}
                    <div className="bg-white rounded-lg overflow-hidden shadow-2xl border border-astro-border/30" data-id="element-1976">
                      {/* Browser Chrome */}
                      <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center gap-2" data-id="element-1977">
                        <div className="flex gap-1.5" data-id="element-1978">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-1979"></div>
                          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-1980"></div>
                          <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-1981"></div>
                        </div>
                        <div className="flex-1 bg-white rounded text-[9px] text-gray-500 px-2 py-1 text-center font-mono truncate" data-id="element-1982">
                          {customDomain || 'app.astrolayb.com'}/reports/env-001
                        </div>
                      </div>

                      {/* App Content — Navbar Preview */}
                      <div className="bg-[#0a0e14] p-4 border-b border-gray-800" data-id="element-1983">
                        <div className="flex items-center justify-between" data-id="element-1984">
                          <div className="flex items-center gap-2" data-id="element-1985">
                            {logoUrl ? <img src={logoUrl} alt={brandName} className="h-5 max-w-[100px] object-contain" data-id="element-1986" /> : <>
                                <Triangle size={14} style={{
                            fill: primaryColor,
                            color: primaryColor
                          }} strokeWidth={2.5} data-id="element-1987" />

                                <span className="text-sm font-bold tracking-tight" style={{
                            color: primaryColor
                          }} data-id="element-1988">
                                  {brandName}
                                </span>
                              </>}
                          </div>
                          <div className="w-6 h-6 rounded-full border border-gray-700 flex items-center justify-center" style={{
                        backgroundColor: `${primaryColor}15`
                      }} data-id="element-1989">
                            <span className="text-[8px] font-bold" style={{
                          color: primaryColor
                        }} data-id="element-1990">
                              {brandName.charAt(0)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Report Content */}
                      <div className="p-5 bg-white min-h-[200px]" data-id="element-1991">
                        <div className="flex justify-between items-start mb-6" data-id="element-1992">
                          <div data-id="element-1993">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-1994">
                              {logoUrl ? <img src={logoUrl} alt={brandName} className="h-4 max-w-[80px] object-contain" data-id="element-1995" /> : <>
                                  <Triangle size={10} style={{
                              fill: primaryColor,
                              color: primaryColor
                            }} strokeWidth={2.5} data-id="element-1996" />

                                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{
                              color: primaryColor
                            }} data-id="element-1997">
                                    {brandName}
                                  </span>
                                </>}
                            </div>
                            <h4 className="text-gray-900 font-bold text-lg mb-1" data-id="element-1998">
                              Security Assessment
                            </h4>
                            <p className="text-gray-500 text-[10px]" data-id="element-1999">
                              Generated for Client XYZ
                            </p>
                          </div>
                          <div className="px-2 py-1 rounded text-[9px] font-bold uppercase" style={{
                        backgroundColor: `${primaryColor}10`,
                        color: primaryColor,
                        border: `1px solid ${primaryColor}30`
                      }} data-id="element-2000">
                            Confidential
                          </div>
                        </div>

                        <div className="space-y-3" data-id="element-2001">
                          <div className="h-2 rounded w-full" style={{
                        backgroundColor: `${primaryColor}15`
                      }} data-id="element-2002"></div>
                          <div className="h-2 bg-gray-100 rounded w-5/6" data-id="element-2003"></div>
                          <div className="h-2 bg-gray-100 rounded w-4/6" data-id="element-2004"></div>
                        </div>

                        <div className="mt-8 grid grid-cols-2 gap-3" data-id="element-2005">
                          <div className="h-16 bg-gray-50 border border-gray-100 rounded" data-id="element-2006"></div>
                          <div className="h-16 bg-gray-50 border border-gray-100 rounded" data-id="element-2007"></div>
                        </div>

                        <div className="mt-8 pt-4 border-t border-gray-100 text-center" data-id="element-2008">
                          <p className="text-[8px] text-gray-400 leading-relaxed" data-id="element-2009">
                            {reportFooter}
                          </p>
                          {!removePoweredBy && brandName !== 'Astrolayb' && <p className="text-[7px] text-gray-300 mt-1" data-id="element-2010">
                              Powered by Astrolayb
                            </p>}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-4 rounded-lg flex items-start gap-3" style={{
                  backgroundColor: `${primaryColor}10`,
                  border: `1px solid ${primaryColor}20`
                }} data-id="element-2011">
                      <Info size={16} className="shrink-0 mt-0.5" style={{
                    color: primaryColor
                  }} data-id="element-2012" />

                      <p className="text-xs leading-relaxed" style={{
                    color: `${primaryColor}cc`
                  }} data-id="element-2013">
                        White-label branding applies to the navbar, generated
                        reports, and email alerts. Try changing the name and
                        color above to see it update in real time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>}

          {activeTab === 'team' && <>
              <div className="flex items-center justify-between mb-2" data-id="element-2014">
                <div data-id="element-2015">
                  <h2 className="text-xl font-bold text-white" data-id="element-2016">Team Members</h2>
                  <p className="text-sm text-astro-text-secondary" data-id="element-2017">
                    Manage users and their access roles
                  </p>
                </div>
                <button className="px-4 py-2 bg-astro-green-dark hover:bg-astro-green text-white text-sm font-medium rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.15)]" data-id="element-2018">
                  Invite Member
                </button>
              </div>

              <div className="bg-astro-card border border-astro-border rounded-lg divide-y divide-astro-border" data-id="element-2019">
                {[{
              name: 'Sarah Chen',
              email: 'sarah.chen@astrolayb.com',
              role: 'Admin',
              initial: 'S'
            }, {
              name: 'James Wilson',
              email: 'j.wilson@astrolayb.com',
              role: 'Member',
              initial: 'J'
            }, {
              name: 'Priya Patel',
              email: 'p.patel@astrolayb.com',
              role: 'Member',
              initial: 'P'
            }, {
              name: 'Alex Rivera',
              email: 'a.rivera@astrolayb.com',
              role: 'Viewer',
              initial: 'A'
            }].map(member => <div key={member.email} className="flex items-center justify-between p-4" data-id="element-2020">
                    <div className="flex items-center gap-3" data-id="element-2021">
                      <div className="w-9 h-9 rounded-full bg-astro-green-dim border border-astro-border flex items-center justify-center" data-id="element-2022">
                        <span className="text-xs font-bold text-astro-green" data-id="element-2023">
                          {member.initial}
                        </span>
                      </div>
                      <div data-id="element-2024">
                        <div className="text-sm font-medium text-white" data-id="element-2025">
                          {member.name}
                        </div>
                        <div className="text-xs text-astro-text-secondary" data-id="element-2026">
                          {member.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2027">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${member.role === 'Admin' ? 'bg-astro-green/10 text-astro-green border-astro-green/20' : 'bg-white/5 text-astro-text-secondary border-white/10'}`} data-id="element-2028">
                        {member.role}
                      </span>
                      {member.role !== 'Admin' && <button className="text-xs text-astro-text-secondary hover:text-astro-danger transition-colors" data-id="element-2029">
                          Remove
                        </button>}
                    </div>
                  </div>)}
              </div>
            </>}

          {activeTab === 'billing' && (() => {
          const billingClients = [{
            id: 1,
            name: 'Acme Corp',
            machineIdentities: 780,
            cloudAccounts: 3,
            clouds: ['AWS', 'Azure', 'GCP']
          }, {
            id: 2,
            name: 'TechStart Inc',
            machineIdentities: 145,
            cloudAccounts: 1,
            clouds: ['Azure']
          }, {
            id: 3,
            name: 'GlobalSecure LLC',
            machineIdentities: 2400,
            cloudAccounts: 5,
            clouds: ['AWS', 'Azure']
          }, {
            id: 4,
            name: 'DataFlow Systems',
            machineIdentities: 89,
            cloudAccounts: 1,
            clouds: ['GCP']
          }, {
            id: 5,
            name: 'CloudNine Solutions',
            machineIdentities: 1200,
            cloudAccounts: 4,
            clouds: ['AWS', 'Azure', 'GCP']
          }, {
            id: 6,
            name: 'NetGuard Pro',
            machineIdentities: 340,
            cloudAccounts: 2,
            clouds: ['AWS', 'Azure']
          }, {
            id: 7,
            name: 'SecureStack',
            machineIdentities: 520,
            cloudAccounts: 2,
            clouds: ['Azure', 'GCP']
          }, {
            id: 8,
            name: 'Vertex Digital',
            machineIdentities: 95,
            cloudAccounts: 1,
            clouds: ['AWS']
          }];
          const getTier = (identities: number) => {
            if (identities <= 250) return {
              name: 'Starter',
              price: 499,
              scan: 'Weekly',
              color: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
              max: 250
            };
            if (identities <= 1000) return {
              name: 'Growth',
              price: 899,
              scan: '2× per week',
              color: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
              max: 1000
            };
            if (identities <= 5000) return {
              name: 'Enterprise',
              price: 1499,
              scan: 'Daily',
              color: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
              max: 5000
            };
            return {
              name: 'Custom',
              price: 0,
              scan: 'Continuous',
              color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
              max: 10000
            };
          };
          const getVolumeDiscount = (count: number) => {
            if (count >= 50) return {
              pct: 0.2,
              label: 'Partnership (50+)',
              next: null
            };
            if (count >= 26) return {
              pct: 0.15,
              label: '26–50 clients',
              next: {
                threshold: 50,
                pct: 20
              }
            };
            if (count >= 11) return {
              pct: 0.1,
              label: '11–25 clients',
              next: {
                threshold: 26,
                pct: 15
              }
            };
            return {
              pct: 0,
              label: 'Standard (1–10)',
              next: {
                threshold: 11,
                pct: 10
              }
            };
          };
          const discount = getVolumeDiscount(billingClients.length);
          const subtotal = billingClients.reduce((s, c) => s + getTier(c.machineIdentities).price, 0);
          const savings = Math.round(subtotal * discount.pct);
          const total = subtotal - savings;
          const totalIdentities = billingClients.reduce((s, c) => s + c.machineIdentities, 0);
          const totalAccounts = billingClients.reduce((s, c) => s + c.cloudAccounts, 0);
          const tierCounts = billingClients.reduce((acc, c) => {
            const t = getTier(c.machineIdentities).name;
            acc[t] = (acc[t] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          return <>
                  <div className="mb-2" data-id="element-2030">
                    <h2 className="text-xl font-bold text-white" data-id="element-2031">
                      Billing & Subscription
                    </h2>
                    <p className="text-sm text-astro-text-secondary" data-id="element-2032">
                      Identity-tier pricing across your managed client portfolio
                    </p>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2033">
                    <div className="p-5 bg-astro-card border-2 border-astro-green/20 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.04)]" data-id="element-2034">
                      <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1" data-id="element-2035">
                        Monthly Total
                      </div>
                      <div className="flex items-baseline gap-2" data-id="element-2036">
                        <span className="text-2xl font-mono font-bold text-white" data-id="element-2037">
                          ${total.toLocaleString()}
                        </span>
                      </div>
                      {discount.pct > 0 && <div className="text-[10px] text-astro-text-secondary mt-1" data-id="element-2038">
                          <span className="line-through" data-id="element-2039">
                            ${subtotal.toLocaleString()}
                          </span>
                          <span className="text-astro-green ml-1.5" data-id="element-2040">
                            Save ${savings.toLocaleString()}
                          </span>
                        </div>}
                      <div className="text-[10px] text-astro-text-secondary mt-0.5" data-id="element-2041">
                        Next bill: Mar 1, 2026
                      </div>
                    </div>

                    <div className="p-5 bg-astro-card border border-astro-border rounded-lg" data-id="element-2042">
                      <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2043">
                        <Users size={10} data-id="element-2044" /> Clients
                      </div>
                      <div className="text-2xl font-mono font-bold text-white" data-id="element-2045">
                        {billingClients.length}
                      </div>
                      <div className="text-[10px] text-astro-text-secondary mt-1" data-id="element-2046">
                        {totalAccounts} cloud accounts
                      </div>
                    </div>

                    <div className="p-5 bg-astro-card border border-astro-border rounded-lg" data-id="element-2047">
                      <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2048">
                        <Shield size={10} data-id="element-2049" /> Machine Identities
                      </div>
                      <div className="text-2xl font-mono font-bold text-white" data-id="element-2050">
                        {totalIdentities.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-astro-text-secondary mt-1" data-id="element-2051">
                        across all clients
                      </div>
                    </div>

                    <div className="p-5 bg-astro-card border border-astro-border rounded-lg relative overflow-hidden" data-id="element-2052">
                      <div className="relative z-10" data-id="element-2053">
                        <div className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-2054">
                          <Zap size={10} className="text-yellow-400" data-id="element-2055" /> Volume
                          Discount
                        </div>
                        <div className="text-2xl font-mono font-bold text-astro-green" data-id="element-2056">
                          {discount.pct * 100}%
                        </div>
                        <div className="text-[10px] text-astro-text-secondary mt-1" data-id="element-2057">
                          {discount.next ? `${discount.next.threshold - billingClients.length} more for ${discount.next.pct}% off` : 'Max discount unlocked'}
                        </div>
                      </div>
                      <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-astro-green/5 rounded-full blur-2xl" data-id="element-2058"></div>
                    </div>
                  </div>

                  {/* Pricing Tiers Reference */}
                  <div className="mt-6 p-5 bg-[#0a0e14] border border-astro-border rounded-lg" data-id="element-2059">
                    <div className="flex items-start gap-3 mb-4" data-id="element-2060">
                      <Info size={16} className="text-astro-green shrink-0 mt-0.5" data-id="element-2061" />

                      <div data-id="element-2062">
                        <h3 className="text-sm font-bold text-white" data-id="element-2063">
                          Identity-Tier Pricing
                        </h3>
                        <p className="text-[11px] text-astro-text-secondary mt-0.5" data-id="element-2064">
                          Per-client pricing based on machine identities
                          discovered. Human identities don't count. Tiers
                          auto-adjust each billing cycle.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2065">
                      {[{
                  name: 'Starter',
                  range: '0–250',
                  price: '$499',
                  freq: 'Weekly',
                  border: 'border-gray-700',
                  text: 'text-gray-400',
                  count: tierCounts['Starter'] || 0
                }, {
                  name: 'Growth',
                  range: '251–1,000',
                  price: '$899',
                  freq: '2×/week',
                  border: 'border-blue-500/30',
                  text: 'text-blue-400',
                  count: tierCounts['Growth'] || 0
                }, {
                  name: 'Enterprise',
                  range: '1,001–5,000',
                  price: '$1,499',
                  freq: 'Daily',
                  border: 'border-purple-500/30',
                  text: 'text-purple-400',
                  count: tierCounts['Enterprise'] || 0
                }, {
                  name: 'Custom',
                  range: '5,000+',
                  price: 'Contact',
                  freq: 'Continuous',
                  border: 'border-yellow-500/30',
                  text: 'text-yellow-400',
                  count: tierCounts['Custom'] || 0
                }].map(tier => <div key={tier.name} className={`p-3 rounded-lg border bg-white/[0.02] ${tier.border}`} data-id="element-2066">
                          <div className="flex items-center justify-between mb-1" data-id="element-2067">
                            <span className={`text-xs font-bold uppercase tracking-wider ${tier.text}`} data-id="element-2068">
                              {tier.name}
                            </span>
                            {tier.count > 0 && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${tier.text} bg-white/5`} data-id="element-2069">
                                {tier.count} client{tier.count !== 1 ? 's' : ''}
                              </span>}
                          </div>
                          <div className="text-[10px] text-astro-text-secondary mb-2" data-id="element-2070">
                            {tier.range} identities
                          </div>
                          <div className="flex justify-between items-end" data-id="element-2071">
                            <span className="text-sm font-bold text-white" data-id="element-2072">
                              {tier.price}
                            </span>
                            <span className="text-[9px] text-astro-text-secondary" data-id="element-2073">
                              {tier.freq}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Client Breakdown Table */}
                  <div className="mt-6 bg-astro-card border border-astro-border rounded-lg overflow-hidden" data-id="element-2074">
                    <div className="px-5 py-3.5 border-b border-astro-border flex justify-between items-center bg-[#0a0e14]" data-id="element-2075">
                      <h3 className="text-sm font-bold text-white flex items-center gap-2" data-id="element-2076">
                        <BarChart3 size={14} className="text-astro-green" data-id="element-2077" />
                        Client Usage & Tiers
                      </h3>
                      <button className="text-[11px] text-astro-green hover:text-astro-green-dark transition-colors font-medium" data-id="element-2078">
                        Export CSV
                      </button>
                    </div>

                    <div className="divide-y divide-astro-border" data-id="element-2079">
                      {billingClients.sort((a, b) => b.machineIdentities - a.machineIdentities).map(client => {
                  const tier = getTier(client.machineIdentities);
                  const pct = Math.min(client.machineIdentities / tier.max * 100, 100);
                  return <div key={client.id} className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-2080">
                              {/* Client Info */}
                              <div className="flex-1 min-w-0" data-id="element-2081">
                                <div className="flex items-center gap-2 mb-1" data-id="element-2082">
                                  <span className="text-sm font-medium text-white" data-id="element-2083">
                                    {client.name}
                                  </span>
                                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tier.color}`} data-id="element-2084">
                                    {tier.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap" data-id="element-2085">
                                  {client.clouds.map(c => <span key={c} className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] text-astro-text-secondary" data-id="element-2086">
                                      {c}
                                    </span>)}
                                  <span className="text-[10px] text-astro-text-secondary" data-id="element-2087">
                                    {client.cloudAccounts} account
                                    {client.cloudAccounts !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>

                              {/* Identity Count + Bar */}
                              <div className="w-48 shrink-0" data-id="element-2088">
                                <div className="flex items-center justify-between mb-1" data-id="element-2089">
                                  <span className="text-xs font-mono text-white" data-id="element-2090">
                                    {client.machineIdentities.toLocaleString()}
                                  </span>
                                  <span className="text-[10px] text-astro-text-secondary" data-id="element-2091">
                                    / {tier.max.toLocaleString()}
                                  </span>
                                </div>
                                <div className="w-full h-1.5 bg-astro-border/50 rounded-full overflow-hidden" data-id="element-2092">
                                  <div className={`h-full rounded-full transition-all ${tier.name === 'Starter' ? 'bg-gray-500' : tier.name === 'Growth' ? 'bg-blue-400' : tier.name === 'Enterprise' ? 'bg-purple-400' : 'bg-yellow-400'}`} style={{
                          width: `${pct}%`
                        }} data-id="element-2093"></div>
                                </div>
                              </div>

                              {/* Scan Freq */}
                              <div className="w-20 shrink-0 text-right hidden sm:block" data-id="element-2094">
                                <span className="text-[10px] text-astro-text-secondary" data-id="element-2095">
                                  {tier.scan}
                                </span>
                              </div>

                              {/* Price */}
                              <div className="w-24 shrink-0 text-right" data-id="element-2096">
                                <span className="text-sm font-mono font-bold text-white" data-id="element-2097">
                                  ${tier.price.toLocaleString()}
                                </span>
                                <span className="text-[10px] text-astro-text-secondary block" data-id="element-2098">
                                  /mo
                                </span>
                              </div>
                            </div>;
                })}
                    </div>

                    {/* Totals Footer */}
                    <div className="border-t border-astro-border bg-[#0a0e14] px-5 py-3" data-id="element-2099">
                      <div className="flex items-center justify-between" data-id="element-2100">
                        <span className="text-xs text-astro-text-secondary" data-id="element-2101">
                          Subtotal ({billingClients.length} clients)
                        </span>
                        <span className="text-sm font-mono font-bold text-white" data-id="element-2102">
                          ${subtotal.toLocaleString()}
                        </span>
                      </div>
                      {discount.pct > 0 && <div className="flex items-center justify-between mt-1" data-id="element-2103">
                          <span className="text-xs text-astro-green" data-id="element-2104">
                            Volume discount ({discount.pct * 100}% ·{' '}
                            {discount.label})
                          </span>
                          <span className="text-sm font-mono font-bold text-astro-green" data-id="element-2105">
                            -${savings.toLocaleString()}
                          </span>
                        </div>}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-astro-border/50" data-id="element-2106">
                        <span className="text-sm font-bold text-white" data-id="element-2107">
                          Monthly Total
                        </span>
                        <span className="text-lg font-mono font-bold text-white" data-id="element-2108">
                          ${total.toLocaleString()}/mo
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Volume Discount Progress */}
                  {discount.next && <div className="mt-6 p-4 bg-astro-card border border-astro-border rounded-lg flex items-center gap-4" data-id="element-2109">
                      <div className="w-10 h-10 rounded-lg bg-yellow-400/10 flex items-center justify-center shrink-0" data-id="element-2110">
                        <Zap size={18} className="text-yellow-400" data-id="element-2111" />
                      </div>
                      <div className="flex-1" data-id="element-2112">
                        <div className="text-sm font-medium text-white" data-id="element-2113">
                          Add {discount.next.threshold - billingClients.length}{' '}
                          more client
                          {discount.next.threshold - billingClients.length !== 1 ? 's' : ''}{' '}
                          to unlock {discount.next.pct}% off
                        </div>
                        <div className="text-[11px] text-astro-text-secondary" data-id="element-2114">
                          You're currently at {billingClients.length} clients (
                          {discount.label}). Reach {discount.next.threshold} for
                          the next discount tier.
                        </div>
                      </div>
                      <div className="w-24 shrink-0" data-id="element-2115">
                        <div className="w-full h-2 bg-astro-border rounded-full overflow-hidden" data-id="element-2116">
                          <div className="h-full bg-yellow-400 rounded-full transition-all" style={{
                    width: `${billingClients.length / discount.next.threshold * 100}%`
                  }} data-id="element-2117"></div>
                        </div>
                        <div className="text-[10px] text-astro-text-secondary text-center mt-1" data-id="element-2118">
                          {billingClients.length}/{discount.next.threshold}
                        </div>
                      </div>
                    </div>}

                  {/* Payment Method */}
                  <div className="mt-6 bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2119">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" data-id="element-2120">
                      <CreditCard size={20} className="text-astro-green" data-id="element-2121" />{' '}
                      Payment Method
                    </h3>
                    <div className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center justify-between" data-id="element-2122">
                      <div className="flex items-center gap-3" data-id="element-2123">
                        <div className="w-10 h-7 bg-white/10 rounded flex items-center justify-center text-[10px] font-bold text-white" data-id="element-2124">
                          VISA
                        </div>
                        <div data-id="element-2125">
                          <div className="text-sm font-medium text-white font-mono" data-id="element-2126">
                            •••• •••• •••• 4242
                          </div>
                          <div className="text-xs text-astro-text-secondary" data-id="element-2127">
                            Expires 12/26
                          </div>
                        </div>
                      </div>
                      <button className="text-xs font-medium text-astro-green hover:text-astro-green-dark transition-colors" data-id="element-2128">
                        Update
                      </button>
                    </div>
                  </div>

                  {/* Invoice History */}
                  <div className="mt-6 bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2129">
                    <h3 className="text-lg font-bold text-white mb-4" data-id="element-2130">
                      Invoice History
                    </h3>
                    <div className="divide-y divide-astro-border" data-id="element-2131">
                      {[{
                  date: 'Feb 1, 2026',
                  amount: `$${total.toLocaleString()}`,
                  clients: billingClients.length,
                  status: 'Paid'
                }, {
                  date: 'Jan 1, 2026',
                  amount: `$${total.toLocaleString()}`,
                  clients: billingClients.length,
                  status: 'Paid'
                }, {
                  date: 'Dec 1, 2025',
                  amount: '$6,293',
                  clients: 7,
                  status: 'Paid'
                }, {
                  date: 'Nov 1, 2025',
                  amount: '$5,394',
                  clients: 6,
                  status: 'Paid'
                }].map(inv => <div key={inv.date} className="flex flex-col sm:flex-row sm:items-center justify-between py-3 gap-2" data-id="element-2132">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-2133">
                            <span className="text-sm text-white" data-id="element-2134">
                              {inv.date}
                            </span>
                            <span className="text-sm font-mono font-bold text-white" data-id="element-2135">
                              {inv.amount}
                            </span>
                            <span className="text-[11px] text-astro-text-secondary" data-id="element-2136">
                              {inv.clients} clients · identity-tier pricing
                            </span>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-2137">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-2138">
                              {inv.status}
                            </span>
                            <button className="text-xs text-astro-text-secondary hover:text-white transition-colors" data-id="element-2139">
                              Download
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </>;
        })()}

          {activeTab === 'api' && <>
              <div className="mb-2" data-id="element-2140">
                <h2 className="text-xl font-bold text-white" data-id="element-2141">
                  API & Integrations
                </h2>
                <p className="text-sm text-astro-text-secondary" data-id="element-2142">
                  Connect Astrolayb with your security stack, ticketing systems,
                  and cloud providers
                </p>
              </div>

              {/* Featured Partner — SecurityScorecard */}
              <div className="bg-gradient-to-r from-[#6C47FF]/10 via-astro-card to-astro-card border border-[#6C47FF]/30 rounded-xl p-6 relative overflow-hidden" data-id="element-2143">
                <div className="absolute top-0 right-0 px-3 py-1 bg-[#6C47FF] text-white text-[10px] font-bold uppercase tracking-wider rounded-bl-lg" data-id="element-2144">
                  Partner Integration
                </div>
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-2145">
                  <div className="w-16 h-16 rounded-xl bg-[#6C47FF]/10 border border-[#6C47FF]/20 flex items-center justify-center shrink-0 p-2" data-id="element-2146">
                    <Shield size={28} className="text-[#6C47FF]" data-id="element-2147" />
                  </div>
                  <div className="flex-1" data-id="element-2148">
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-2149">
                      SecurityScorecard
                    </h3>
                    <p className="text-sm text-astro-text-secondary leading-relaxed mb-3" data-id="element-2150">
                      Enrich your vulnerability data with external attack
                      surface ratings. Automatically correlate SecurityScorecard
                      grades with internal scan findings for a unified risk view
                      across all managed clients.
                    </p>
                    <div className="flex flex-wrap items-center gap-2" data-id="element-2151">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#6C47FF]/10 text-[#6C47FF] border border-[#6C47FF]/20" data-id="element-2152">
                        External Risk Ratings
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#6C47FF]/10 text-[#6C47FF] border border-[#6C47FF]/20" data-id="element-2153">
                        Supply Chain Risk
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#6C47FF]/10 text-[#6C47FF] border border-[#6C47FF]/20" data-id="element-2154">
                        Continuous Monitoring
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#6C47FF]/10 text-[#6C47FF] border border-[#6C47FF]/20" data-id="element-2155">
                        Compliance Mapping
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-2" data-id="element-2156">
                    <button className="px-5 py-2.5 bg-[#6C47FF] text-white text-sm font-bold rounded-lg hover:bg-[#5a38e0] transition-colors shadow-[0_0_15px_rgba(108,71,255,0.25)]" data-id="element-2157">
                      Configure Integration
                    </button>
                    <span className="text-[10px] text-astro-text-secondary" data-id="element-2158">
                      Requires SecurityScorecard API key
                    </span>
                  </div>
                </div>
              </div>

              {/* API Keys */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2159">
                <div className="flex items-center justify-between mb-6" data-id="element-2160">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-2161">
                    <Key size={20} className="text-astro-green" data-id="element-2162" />
                    API Keys
                  </h3>
                  <button className="px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors" data-id="element-2163">
                    Generate New Key
                  </button>
                </div>
                <div className="space-y-3" data-id="element-2164">
                  <div className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center justify-between" data-id="element-2165">
                    <div data-id="element-2166">
                      <div className="text-sm font-bold text-white" data-id="element-2167">
                        Production Key
                      </div>
                      <div className="text-xs text-astro-text-secondary font-mono mt-1" data-id="element-2168">
                        pk_live_...8a92
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2169">
                      <span className="text-[10px] bg-astro-green/10 text-astro-green px-2 py-0.5 rounded border border-astro-green/20" data-id="element-2170">
                        Active
                      </span>
                      <button className="text-astro-text-secondary hover:text-white p-1" data-id="element-2171">
                        <Key size={14} data-id="element-2172" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center justify-between" data-id="element-2173">
                    <div data-id="element-2174">
                      <div className="text-sm font-bold text-white" data-id="element-2175">
                        Staging Key
                      </div>
                      <div className="text-xs text-astro-text-secondary font-mono mt-1" data-id="element-2176">
                        pk_test_...3f17
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2177">
                      <span className="text-[10px] bg-white/5 text-astro-text-secondary px-2 py-0.5 rounded border border-white/10" data-id="element-2178">
                        Test
                      </span>
                      <button className="text-astro-text-secondary hover:text-white p-1" data-id="element-2179">
                        <Key size={14} data-id="element-2180" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security & Vulnerability Integrations */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2181">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2" data-id="element-2182">
                  <Shield size={20} className="text-astro-green" data-id="element-2183" />
                  Security & Vulnerability
                </h3>
                <p className="text-xs text-astro-text-secondary mb-6" data-id="element-2184">
                  Enrich scan data with third-party vulnerability intelligence
                  and risk ratings
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2185">
                  {[{
                name: 'CrowdStrike Falcon',
                desc: 'Endpoint detection and response data correlation',
                status: 'available',
                color: '#E3242B'
              }, {
                name: 'Qualys VMDR',
                desc: 'Import vulnerability scan results and asset inventory',
                status: 'available',
                color: '#ED1C24'
              }, {
                name: 'Tenable.io',
                desc: 'Sync vulnerability data and compliance benchmarks',
                status: 'available',
                color: '#00B4D8'
              }, {
                name: 'Rapid7 InsightVM',
                desc: 'Correlate exposure data with identity findings',
                status: 'coming',
                color: '#FF6D2E'
              }].map(integration => <div key={integration.name} className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center gap-4" data-id="element-2186">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{
                  backgroundColor: `${integration.color}15`,
                  border: `1px solid ${integration.color}25`
                }} data-id="element-2187">
                        <Shield size={18} style={{
                    color: integration.color
                  }} data-id="element-2188" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2189">
                        <div className="text-sm font-bold text-white" data-id="element-2190">
                          {integration.name}
                        </div>
                        <div className="text-xs text-astro-text-secondary mt-0.5" data-id="element-2191">
                          {integration.desc}
                        </div>
                      </div>
                      {integration.status === 'available' ? <button className="px-3 py-1.5 text-xs font-bold text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors shrink-0" data-id="element-2192">
                          Connect
                        </button> : <span className="text-[10px] font-bold uppercase tracking-wider text-astro-text-secondary/50 shrink-0" data-id="element-2193">
                          Coming Soon
                        </span>}
                    </div>)}
                </div>
              </div>

              {/* SIEM & Monitoring */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2194">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2" data-id="element-2195">
                  <BarChart3 size={20} className="text-astro-green" data-id="element-2196" />
                  SIEM & Monitoring
                </h3>
                <p className="text-xs text-astro-text-secondary mb-6" data-id="element-2197">
                  Forward findings and alerts to your monitoring and analytics
                  platforms
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2198">
                  {[{
                name: 'Splunk',
                desc: 'Stream scan findings and identity alerts to Splunk indexes',
                status: 'connected',
                color: '#65A637'
              }, {
                name: 'Microsoft Sentinel',
                desc: 'Push security events to Azure Sentinel workspace',
                status: 'available',
                color: '#0078D4'
              }, {
                name: 'Datadog',
                desc: 'Send security metrics and custom events to Datadog',
                status: 'available',
                color: '#632CA6'
              }, {
                name: 'Elastic SIEM',
                desc: 'Export findings to Elasticsearch for custom dashboards',
                status: 'available',
                color: '#00BFB3'
              }].map(integration => <div key={integration.name} className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center gap-4" data-id="element-2199">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{
                  backgroundColor: `${integration.color}15`,
                  border: `1px solid ${integration.color}25`
                }} data-id="element-2200">
                        <BarChart3 size={18} style={{
                    color: integration.color
                  }} data-id="element-2201" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2202">
                        <div className="flex items-center gap-2" data-id="element-2203">
                          <span className="text-sm font-bold text-white" data-id="element-2204">
                            {integration.name}
                          </span>
                          {integration.status === 'connected' && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-2205">
                              Connected
                            </span>}
                        </div>
                        <div className="text-xs text-astro-text-secondary mt-0.5" data-id="element-2206">
                          {integration.desc}
                        </div>
                      </div>
                      {integration.status === 'connected' ? <button className="px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors shrink-0" data-id="element-2207">
                          Configure
                        </button> : <button className="px-3 py-1.5 text-xs font-bold text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors shrink-0" data-id="element-2208">
                          Connect
                        </button>}
                    </div>)}
                </div>
              </div>

              {/* Ticketing & Workflow */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2209">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2" data-id="element-2210">
                  <ArrowRightLeft size={20} className="text-astro-green" data-id="element-2211" />
                  Ticketing & Workflow
                </h3>
                <p className="text-xs text-astro-text-secondary mb-6" data-id="element-2212">
                  Auto-create remediation tickets and sync status with your
                  project management tools
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2213">
                  {[{
                name: 'Jira',
                desc: 'Create issues from findings and sync remediation status',
                status: 'connected',
                color: '#0052CC'
              }, {
                name: 'ServiceNow',
                desc: 'Open incidents and change requests from scan results',
                status: 'available',
                color: '#81B5A1'
              }, {
                name: 'PagerDuty',
                desc: 'Trigger on-call alerts for critical security findings',
                status: 'available',
                color: '#06AC38'
              }, {
                name: 'Slack',
                desc: 'Send real-time scan notifications to channels',
                status: 'connected',
                color: '#4A154B'
              }].map(integration => <div key={integration.name} className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center gap-4" data-id="element-2214">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{
                  backgroundColor: `${integration.color}15`,
                  border: `1px solid ${integration.color}25`
                }} data-id="element-2215">
                        <ArrowRightLeft size={18} style={{
                    color: integration.color
                  }} data-id="element-2216" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2217">
                        <div className="flex items-center gap-2" data-id="element-2218">
                          <span className="text-sm font-bold text-white" data-id="element-2219">
                            {integration.name}
                          </span>
                          {integration.status === 'connected' && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-2220">
                              Connected
                            </span>}
                        </div>
                        <div className="text-xs text-astro-text-secondary mt-0.5" data-id="element-2221">
                          {integration.desc}
                        </div>
                      </div>
                      {integration.status === 'connected' ? <button className="px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors shrink-0" data-id="element-2222">
                          Configure
                        </button> : <button className="px-3 py-1.5 text-xs font-bold text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors shrink-0" data-id="element-2223">
                          Connect
                        </button>}
                    </div>)}
                </div>
              </div>

              {/* MSP Platform Integrations */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2224">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2" data-id="element-2225">
                  <Building2 size={20} className="text-astro-green" data-id="element-2226" />
                  MSP Platform Integrations
                </h3>
                <p className="text-xs text-astro-text-secondary mb-6" data-id="element-2227">
                  Sync clients, tickets, and billing with your PSA and RMM
                  platforms
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2228">
                  {[{
                name: 'ConnectWise',
                desc: 'Sync client records, auto-create service tickets from findings, and push billing data to ConnectWise Manage.',
                status: 'available',
                logo: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/sfrhj22ezixep42drbpn6b/1d6095db-ffe5-4bb1-be8b-4539de22bb13/connectwise-horiz-master.png',
                tags: ['PSA', 'RMM', 'Billing'],
                isJpg: false
              }, {
                name: 'HaloPSA',
                desc: 'Create and manage remediation tickets, map clients to Halo tenants, and automate SLA-driven workflows.',
                status: 'available',
                logo: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/sfrhj22ezixep42drbpn6b/1d6095db-ffe5-4bb1-be8b-4539de22bb13/halo-psa-logo.svg',
                tags: ['PSA', 'Ticketing', 'SLA'],
                isJpg: false
              }, {
                name: 'Autotask (Datto)',
                desc: 'Push scan findings as Autotask tickets, sync client companies, and track remediation progress.',
                status: 'available',
                logo: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/sfrhj22ezixep42drbpn6b/1d6095db-ffe5-4bb1-be8b-4539de22bb13/Autotask-Logo.jpg',
                tags: ['PSA', 'Ticketing', 'Workflow'],
                isJpg: true
              }].map(integration => <div key={integration.name} className="p-5 bg-[#0a0e14] border border-astro-border rounded-lg flex flex-col" data-id="element-2229">
                      <div className="w-full h-12 mb-4 flex items-center" data-id="element-2230">
                        <img src={integration.logo} alt={integration.name} className={integration.isJpg ? 'h-8 max-w-[140px] object-contain rounded bg-white px-2 py-1' : 'h-8 max-w-[140px] object-contain brightness-0 invert opacity-90'} data-id="element-2231" />
                      </div>
                      <div className="flex-1" data-id="element-2232">
                        <div className="text-sm font-bold text-white mb-1" data-id="element-2233">
                          {integration.name}
                        </div>
                        <p className="text-xs text-astro-text-secondary leading-relaxed mb-3" data-id="element-2234">
                          {integration.desc}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-2235">
                          {integration.tags.map(tag => <span key={tag} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-2236">
                              {tag}
                            </span>)}
                        </div>
                      </div>
                      <button className="w-full py-2 text-xs font-bold text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-2237">
                        Connect
                      </button>
                    </div>)}
                </div>
                <div className="mt-4 p-3 bg-astro-green/5 border border-astro-green/15 rounded-lg flex items-start gap-3" data-id="element-2238">
                  <Info size={14} className="text-astro-green shrink-0 mt-0.5" data-id="element-2239" />

                  <p className="text-xs text-astro-green/80 leading-relaxed" data-id="element-2240">
                    Astrolayb is listed on{' '}
                    <span className="font-bold text-astro-green" data-id="element-2241">
                      MSPintegrations.com
                    </span>{' '}
                    — connect your PSA platform to automatically sync clients,
                    push remediation tickets, and streamline your security
                    operations workflow.
                  </p>
                </div>
              </div>

              {/* Cloud Providers */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2242">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2" data-id="element-2243">
                  <Globe size={20} className="text-astro-green" data-id="element-2244" />
                  Cloud Providers
                </h3>
                <p className="text-xs text-astro-text-secondary mb-6" data-id="element-2245">
                  Native integrations for identity scanning and continuous
                  monitoring
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2246">
                  {[{
                name: 'AWS',
                desc: 'IAM, Organizations, CloudTrail',
                status: 'connected',
                accounts: 12,
                color: '#FF9900'
              }, {
                name: 'Microsoft Azure',
                desc: 'Entra ID, Subscriptions, Defender',
                status: 'connected',
                accounts: 8,
                color: '#0078D4'
              }, {
                name: 'Google Cloud',
                desc: 'IAM, Cloud Identity, Security Command Center',
                status: 'available',
                accounts: 0,
                color: '#4285F4'
              }].map(cloud => <div key={cloud.name} className="p-5 bg-[#0a0e14] border border-astro-border rounded-lg" data-id="element-2247">
                      <div className="flex items-center gap-3 mb-3" data-id="element-2248">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                    backgroundColor: `${cloud.color}15`,
                    border: `1px solid ${cloud.color}25`
                  }} data-id="element-2249">
                          <Globe size={18} style={{
                      color: cloud.color
                    }} data-id="element-2250" />
                        </div>
                        <div data-id="element-2251">
                          <div className="text-sm font-bold text-white" data-id="element-2252">
                            {cloud.name}
                          </div>
                          <div className="text-[10px] text-astro-text-secondary" data-id="element-2253">
                            {cloud.desc}
                          </div>
                        </div>
                      </div>
                      {cloud.status === 'connected' ? <div className="flex items-center justify-between" data-id="element-2254">
                          <div className="flex items-center gap-2" data-id="element-2255">
                            <CheckCircle2 size={12} className="text-astro-green" data-id="element-2256" />

                            <span className="text-xs text-astro-green font-medium" data-id="element-2257">
                              {cloud.accounts} accounts connected
                            </span>
                          </div>
                          <button className="text-xs text-astro-green hover:underline font-medium" data-id="element-2258">
                            Manage
                          </button>
                        </div> : <button className="w-full py-2 text-xs font-bold text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-2259">
                          Connect Accounts
                        </button>}
                    </div>)}
                </div>
              </div>

              {/* Webhooks */}
              <div className="bg-astro-card border border-astro-border rounded-lg p-6" data-id="element-2260">
                <div className="flex items-center justify-between mb-6" data-id="element-2261">
                  <div data-id="element-2262">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-2263">
                      <Code size={20} className="text-astro-green" data-id="element-2264" />
                      Webhooks
                    </h3>
                    <p className="text-xs text-astro-text-secondary mt-1" data-id="element-2265">
                      Receive real-time HTTP callbacks when events occur
                    </p>
                  </div>
                  <button className="px-3 py-1.5 text-xs font-bold text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors" data-id="element-2266">
                    Add Webhook
                  </button>
                </div>
                <div className="space-y-3" data-id="element-2267">
                  <div className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center justify-between" data-id="element-2268">
                    <div data-id="element-2269">
                      <div className="text-sm font-bold text-white" data-id="element-2270">
                        Scan Completed
                      </div>
                      <div className="text-xs text-astro-text-secondary font-mono mt-1" data-id="element-2271">
                        https://hooks.example.com/astrolayb/scan-complete
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2272">
                      <span className="text-[10px] bg-astro-green/10 text-astro-green px-2 py-0.5 rounded border border-astro-green/20" data-id="element-2273">
                        Active
                      </span>
                      <button className="text-xs text-astro-text-secondary hover:text-white transition-colors" data-id="element-2274">
                        Edit
                      </button>
                    </div>
                  </div>
                  <div className="p-4 bg-[#0a0e14] border border-astro-border rounded-lg flex items-center justify-between" data-id="element-2275">
                    <div data-id="element-2276">
                      <div className="text-sm font-bold text-white" data-id="element-2277">
                        Critical Finding Detected
                      </div>
                      <div className="text-xs text-astro-text-secondary font-mono mt-1" data-id="element-2278">
                        https://hooks.example.com/astrolayb/critical-alert
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2279">
                      <span className="text-[10px] bg-astro-green/10 text-astro-green px-2 py-0.5 rounded border border-astro-green/20" data-id="element-2280">
                        Active
                      </span>
                      <button className="text-xs text-astro-text-secondary hover:text-white transition-colors" data-id="element-2281">
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>}

          {/* Placeholders for other tabs */}
          {false && <div className="flex flex-col items-center justify-center h-64 bg-astro-card border border-astro-border rounded-lg text-astro-text-secondary" data-id="element-2282">
              <div className="w-12 h-12 rounded-full bg-astro-green/10 flex items-center justify-center mb-4" data-id="element-2283">
                {tabs.find(t => t.id === activeTab)?.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-1" data-id="element-2284">
                {tabs.find(t => t.id === activeTab)?.label}
              </h3>
              <p className="text-sm" data-id="element-2285">
                This section is currently under development.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}